import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './PhysioHelp.css';
import NavBarTop from '../components/NavBarTop';
import BottomSheet from '../components/BottomSheet';
import '../components/BottomSheet.css';

const PhysioHelp = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        fetchHelps();
    }, []);

    const [loading, setLoading] = useState(true);
    const [allResources, setAllResources] = useState([]);
    const [activeCategory, setActiveCategory] = useState('All');
    const [categories, setCategories] = useState(['All']);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    const itemsPerPage = 8;

    const fetchHelps = async () => {
        setLoading(true);
        try {
            const response = await axios.get('https://physiodirectory.in/api/physio-help');
            setAllResources(response.data.helps.data);
            if (response.data.categories) {
                setCategories(['All', ...response.data.categories]);
            }
        } catch (error) {
            console.error('Error fetching physio help:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter Logic
    const filteredResources = allResources.filter(r => {
        const matchesCategory = activeCategory === 'All' || r.category === activeCategory;
        const matchesSearch = r.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                              (r.description && r.description.toLowerCase().includes(searchTerm.toLowerCase()));
        return matchesCategory && matchesSearch;
    });

    // Pagination Logic
    const totalPages = Math.ceil(filteredResources.length / itemsPerPage);
    const currentItems = filteredResources.slice(
        (currentPage - 1) * itemsPerPage, 
        currentPage * itemsPerPage
    );

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            window.scrollTo(0, 0);
        }
    };

    const handleVisit = (url) => {
        if (!url) return;
        if (window.Native && window.Native.openExternal) {
            window.Native.openExternal({ url });
        } else {
            window.open(url, '_blank');
        }
    };

    return (
        <div className="mobile-page-container">
            {/* Top NavBar with Back and Title and Filter Button */}
            <NavBarTop 
                title="Physio Help"
                right={
                    <button className="btn btn-link p-0" style={{fontSize: '1.5rem'}} onClick={() => setFilterSheetOpen(true)}>
                        <i className="bi bi-funnel"></i>
                    </button>
                }
            />
            <BottomSheet open={filterSheetOpen} onClose={() => setFilterSheetOpen(false)} className="physiohelp-bottom-sheet">
                <form onSubmit={e => e.preventDefault()} className="mb-3">
                    <label className="form-label fw-bold">Search</label>
                    <div className="search-input-wrapper">
                        <i className="bi bi-search"></i>
                        <input 
                            type="text" 
                            className="form-control modern-search-input" 
                            placeholder="Search Resources..."
                            value={searchTerm}
                            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                </form>
                <label className="form-label fw-bold">Category</label>
                <div className="category-scroll-container mt-2 mb-0 ps-0">
                    {categories.map(cat => (
                        <div 
                            key={cat}
                            className={`category-pill ${activeCategory === cat ? 'active' : ''}`}
                            onClick={() => { setActiveCategory(cat); setCurrentPage(1); }}
                        >
                            {cat}
                        </div>
                    ))}
                </div>
            </BottomSheet>
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <div className="container-fluid px-3 pt-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold mb-0 text-dark">Resources</h5>
                        <span className="text-muted small">{filteredResources.length} Items</span>
                    </div>
                    {/* Resource Grid */}
                    <div className="row g-3">
                        {currentItems.length > 0 ? (
                            currentItems.map((item) => (
                                <div className="col-6 col-md-4 col-lg-3" key={item.id}>
                                    <div 
                                        onClick={() => handleVisit(item.url)} 
                                        className="text-decoration-none h-100 d-block cursor-pointer"
                                        style={{cursor: 'pointer'}}
                                    >
                                        <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden card-hover">
                                            <div className="position-relative bg-light d-flex align-items-center justify-content-center p-4" style={{ height: '140px' }}>
                                                {item.logo_url ? (
                                                    <img src={item.logo_url} alt={item.title} className="img-fluid" style={{maxHeight:'80px', objectFit:'contain'}}/>
                                                ) : (
                                                    <i className="bi bi-journal-medical text-primary opacity-50 display-4"></i>
                                                )}
                                            </div>
                                            <div className="card-body p-3 d-flex flex-column">
                                                <div className="text-primary fw-bold text-uppercase mb-1" style={{fontSize: '0.65rem', letterSpacing: '0.5px'}}>{item.category}</div>
                                                <h6 className="fw-bold text-dark mb-2 text-truncate" style={{fontSize: '0.9rem'}}>{item.title}</h6>
                                                <p className="text-muted small mb-3 text-truncate-2" style={{fontSize: '0.75rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>{item.description}</p>
                                                <div className="mt-auto d-flex align-items-center justify-content-between">
                                                    <span className="text-primary fw-semibold small" style={{fontSize: '0.8rem'}}>Visit</span>
                                                    <div className="btn btn-sm btn-light rounded-circle shadow-sm d-flex align-items-center justify-content-center" style={{width: '28px', height: '28px', padding: 0}}>
                                                        <i className="bi bi-arrow-up-right text-primary" style={{ fontSize: '0.7rem' }}></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-12 text-center py-5">
                                <i className="bi bi-folder-x display-1 text-muted opacity-25"></i>
                                <p className="text-muted mt-2">No resources found.</p>
                            </div>
                        )}
                    </div>
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center align-items-center mt-4 mb-5 pb-5 gap-3">
                            <button 
                                className={`btn rounded-circle shadow p-0 d-flex align-items-center justify-content-center ${currentPage === 1 ? 'btn-light text-muted opacity-50' : 'btn-primary text-white'}`}
                                style={{width: '40px', height: '40px'}}
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                <i className="bi bi-chevron-left"></i>
                            </button>
                            <span className="fw-bold text-dark mx-2 small">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button 
                                className={`btn rounded-circle shadow p-0 d-flex align-items-center justify-content-center ${currentPage === totalPages ? 'btn-light text-muted opacity-50' : 'btn-primary text-white'}`}
                                style={{width: '40px', height: '40px'}}
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                 <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PhysioHelp;
